"use client";

import detail480 from "@/assets/detail-macro-480.webp";
import detail768 from "@/assets/detail-macro-768.webp";
import detail960 from "@/assets/detail-macro-960.webp";
import work3480 from "@/assets/work-3-480.webp";
import work3768 from "@/assets/work-3-768.webp";
import work3960 from "@/assets/work-3-960.webp";
import { Cta } from "./cta";
import { MaskedHeadline, Reveal } from "./motion-primitives";
import { useQuote } from "./quote-context";
import { ResponsiveImage, srcSet } from "./responsive-image";

const SPLIT_SIZES = "(min-width: 1024px) 25vw, 50vw";
const DETAIL_SRCSET = srcSet([
  [detail480, 480],
  [detail768, 768],
  [detail960, 960],
]);
const WORK3_SRCSET = srcSet([
  [work3480, 480],
  [work3768, 768],
  [work3960, 960],
]);

const POINTS = [
  "Personalized project guidance",
  "Clear communication",
  "Quality-focused engraving",
  "Solutions based on the project's purpose",
  "Support for business and promotional needs",
];

export function BusinessSolutions() {
  const { goToQuote } = useQuote();

  return (
    <section className="relative overflow-hidden bg-ink py-24 sm:py-32">
      <div
        aria-hidden
        className="absolute top-0 left-1/2 hidden h-full w-px origin-top bg-gradient-to-b from-transparent via-brass to-transparent lg:block"
      />
      <div className="mx-auto grid max-w-[1400px] items-center gap-14 px-5 lg:grid-cols-2 lg:gap-24">
        <div>
          <p className="eyebrow text-brass">Business & promotional</p>
          <h2 className="mt-5 font-display text-[clamp(2.1rem,5vw,4rem)] leading-[0.98] font-bold text-softwhite">
            <MaskedHeadline lines={["When your name is on it,", "every detail matters."]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-7 max-w-lg text-base leading-relaxed text-softwhite/70">
              From customer appreciation and employee recognition to events and
              promotional campaigns, Mills Engraving helps businesses turn ordinary
              products into more meaningful brand experiences.
            </p>
          </Reveal>

          <ul className="mt-10 space-y-0">
            {POINTS.map((p, i) => (
              <Reveal as="li" key={p} delay={i * 0.07}>
                <div className="flex items-center gap-4 border-b border-softwhite/10 py-4">
                  <span className="size-1.5 rotate-45 bg-brass" aria-hidden />
                  <span className="text-sm font-medium text-softwhite/85">{p}</span>
                </div>
              </Reveal>
            ))}
          </ul>

          <div className="mt-10">
            <Cta onClick={() => goToQuote({ audience: "Customers" })}>
              Discuss a Business Project
            </Cta>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-6">
          <div className="aspect-[3/4] overflow-hidden">
            <ResponsiveImage
              src={detail480}
              srcSet={DETAIL_SRCSET}
              sizes={SPLIT_SIZES}
              alt="Close-up of a stainless steel tumbler laser-engraved with a circular SC consulting logo"
              width={720}
              height={960}
              className="size-full object-cover"
            />
          </div>
          <div className="mt-10 aspect-[3/4] overflow-hidden">
            <ResponsiveImage
              src={work3480}
              srcSet={WORK3_SRCSET}
              sizes={SPLIT_SIZES}
              alt="Matching grey snapback hat and black tumbler engraved with a Prairie Plains logo"
              width={960}
              height={803}
              className="size-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}